// SelfScoreRollupCard — Phase 2.2 of the Federal Template Parity sprint.
// Shows the self-score distribution (Red/Yellow/Green/Blue + un-scored)
// AGGREGATED BY TASK AREA, sourced from /hierarchy-summary.
//
// Designed for the Go/No-Go dashboard. Hides itself if the pursuit hasn't
// got any extracted requirements with task_area set yet (avoid noise).
//
// Click a Task Area row → navigate to Opportunity Intake (filtered view
// is left as a future enhancement; for now the user lands on the Requirements
// table with the Group-by-Area toggle they can engage).

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Button } from '../ui/button';
import { Layers, ExternalLink, ChevronDown, ChevronRight, Info, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import BulkBucketDialog from './BulkBucketDialog';

const SEG_COLOR = {
  red: 'bg-red-500',
  yellow: 'bg-amber-400',
  green: 'bg-emerald-500',
  blue: 'bg-blue-500',
  unscored: 'bg-slate-200',
};

const SEG_LABEL = {
  red: 'Red — No Capability',
  yellow: 'Yellow — Some Understanding',
  green: 'Green — w/ 1 PP',
  blue: 'Blue — w/ 2+ PPs',
  unscored: 'Un-scored',
};

function StackedBar({ distribution, total, dataTestIdPrefix }) {  if (!total) return null;
  const segs = ['red', 'yellow', 'green', 'blue', 'unscored'];
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-1.5 rounded overflow-hidden ring-1 ring-slate-200">
        {segs.map(key => {
          const count = distribution[key] || 0;
          const pct = (count / total) * 100;
          if (pct <= 0) return null;
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <div
                  data-testid={`${dataTestIdPrefix}-seg-${key}`}
                  className={cn(SEG_COLOR[key], 'transition-opacity hover:opacity-80')}
                  style={{ width: `${pct}%` }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <p className="font-semibold">{SEG_LABEL[key]}</p>
                <p className="opacity-90">{count} of {total} ({Math.round(pct)}%)</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

// Empty-state rollup card — shown when no requirements OR all bucketed Unassigned.
// v3.18.3: differentiates three states so users get an actionable message:
//   1. 0 requirements        → "Awaiting requirement extraction"
//   2. N reqs, 0 named TAs   → "{N} bucketed as Unassigned — tag them"
// Returns the correct copy + CTA per state.
function EmptyRollupCard({ onNavigate, totalRequirements = 0, unassignedCount = 0, pursuitId, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isClickable = typeof onNavigate === 'function';
  const hasReqsButNoNamedTAs = totalRequirements > 0;

  const headerCopy = hasReqsButNoNamedTAs
    ? `${unassignedCount || totalRequirements} requirement${(unassignedCount || totalRequirements) === 1 ? '' : 's'} need Task Area`
    : 'Awaiting requirement extraction';

  const bodyCopy = hasReqsButNoNamedTAs ? (
    <>
      You have <strong className="text-slate-700">{totalRequirements}</strong> extracted
      requirement{totalRequirements === 1 ? '' : 's'}, but{' '}
      <strong className="text-slate-700">none are tagged to a federal Task Area</strong>{' '}
      yet — they're all sitting in <em>Unassigned</em>. Open the Intake screen, click
      the <span className="font-mono text-slate-700">+ Task Area</span> chip on each
      row, and pick a name (or run the AI Shredder to auto-bucket them).
    </>
  ) : (
    <>
      Groups the <strong className="text-slate-700">0–3 SME walk scores</strong> by
      federal <strong className="text-slate-700">Task Area → Sub-Area</strong> so
      reviewers can see where capability is strong vs thin. Populated automatically
      once requirements are extracted and tagged.
    </>
  );

  const ctaCopy = hasReqsButNoNamedTAs
    ? 'Tag Task Areas in Intake'
    : 'Run the AI Shredder on Intake';

  return (
    <Card data-testid="self-score-rollup-card-empty" className="border-slate-200">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="rollup-empty-toggle"
            className="w-full flex items-center gap-2 p-3 hover:bg-slate-50/60 transition-colors text-left"
          >
            {open
              ? <ChevronDown className="w-4 h-4 text-slate-400" />
              : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <Layers className={cn('w-4 h-4', hasReqsButNoNamedTAs ? 'text-amber-500' : 'text-slate-400')} />
            <span className="text-sm font-semibold text-slate-600">
              Self-Score Roll-up by Task Area
            </span>
            <span
              className={cn(
                'text-[11px] italic ml-auto',
                hasReqsButNoNamedTAs ? 'text-amber-700' : 'text-slate-400',
              )}
              data-testid="rollup-empty-status-text"
            >
              {headerCopy}
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4 text-[12px] text-slate-500 space-y-2">
            <p className="flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 text-cyan-500 mt-0.5 shrink-0" />
              <span>{bodyCopy}</span>
            </p>
            {isClickable && (
              <button
                type="button"
                onClick={onNavigate}
                data-testid="rollup-empty-cta"
                className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-800 hover:underline"
              >
                {ctaCopy} <ExternalLink className="w-3 h-3" />
              </button>
            )}
            {/* v3.18.4 — Bulk-bucket Unassigned with AI (only when there ARE reqs) */}
            {hasReqsButNoNamedTAs && pursuitId && (
              <div className="pt-1">
                <Button
                  size="sm"
                  onClick={() => setBulkOpen(true)}
                  className="h-7 text-xs bg-cyan-700 hover:bg-cyan-800 text-white"
                  data-testid="rollup-empty-bulk-bucket-btn"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  Bulk-bucket {totalRequirements} with AI
                </Button>
                <p className="text-[10px] text-slate-400 mt-1 italic">
                  Runs the AI Shredder over all Unassigned rows and proposes a Task Area for each — you confirm before saving.
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
      {pursuitId && (
        <BulkBucketDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          pursuitId={pursuitId}
          onApplied={() => { if (typeof onRefresh === 'function') onRefresh(); }}
        />
      )}
    </Card>
  );
}

export function SelfScoreRollupCard({ pursuitId, authToken, apiUrl, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // v3.18.4 — bumped to force a re-fetch after the bulk-bucket dialog applies.
  const [refreshKey, setRefreshKey] = useState(0);
  const [bulkOpen, setBulkOpen] = useState(false);
  const triggerRefresh = () => setRefreshKey(k => k + 1);

  useEffect(() => {
    if (!pursuitId || !authToken) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${apiUrl}/api/ai/requirements/${pursuitId}/hierarchy-summary`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (r.ok && !cancelled) setData(await r.json());
      } catch (_) {
        // Silent — this card auto-hides on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pursuitId, authToken, apiUrl, refreshKey]);

  if (loading || !data) return null;
  // v3.11.5 — Empty States UX Refactor:
  //   Instead of returning null when there are no requirements or no Task
  //   Areas yet, render a collapsed empty-state card. Federal-template
  //   tools stay discoverable on fresh pursuits.
  const isEmpty = !data.total_requirements || data.task_area_count === 0;
  if (isEmpty) {
    return (
      <EmptyRollupCard
        onNavigate={onNavigate}
        totalRequirements={data.total_requirements || 0}
        unassignedCount={data.unassigned_count || 0}
        pursuitId={pursuitId}
        onRefresh={triggerRefresh}
      />
    );
  }

  const taskAreas = data.task_areas || [];
  const unassignedCount = data.unassigned_count || 0;

  return (
    <Card data-testid="self-score-rollup-card" className="border-cyan-100">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-slate-800">
          <Layers className="w-4 h-4 text-cyan-600" />
          Self-Score Roll-up by Task Area
          <span className="text-[11px] font-normal text-slate-400 ml-1">
            ({data.task_area_count} area{data.task_area_count === 1 ? '' : 's'} · {data.total_requirements} req{data.total_requirements === 1 ? '' : 's'})
          </span>
          {/* v3.18.4 — Bulk-bucket Unassigned with AI button */}
          {unassignedCount > 0 && (
            <Button
              size="sm"
              onClick={() => setBulkOpen(true)}
              className="ml-auto h-7 text-xs bg-cyan-700 hover:bg-cyan-800 text-white"
              data-testid="rollup-bulk-bucket-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              Bulk-bucket {unassignedCount} Unassigned with AI
            </Button>
          )}
        </CardTitle>
        <p className="text-[11px] text-slate-500 -mt-1 ml-6">
          Bottom-up SME walk grouped by federal Task Area. Click a row to review.
        </p>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5">
        {taskAreas.map(ta => {
          const isClickable = typeof onNavigate === 'function';
          const Wrapper = isClickable ? 'button' : 'div';
          return (
            <Wrapper
              key={ta.name}
              type={isClickable ? 'button' : undefined}
              onClick={isClickable ? () => onNavigate(ta) : undefined}
              data-testid={`rollup-row-${ta.name.replace(/\s+/g, '-')}`}
              className={cn(
                'w-full text-left rounded p-2 transition-colors',
                isClickable && 'hover:bg-slate-50 cursor-pointer group',
                ta.is_unassigned && 'opacity-75'
              )}
            >
              <div className="flex items-center justify-between mb-1 text-[11px]">
                <span className={cn(
                  'font-medium flex items-center gap-1',
                  ta.is_unassigned ? 'text-slate-500 italic' : 'text-slate-700'
                )}>
                  {ta.name}
                  {isClickable && (
                    <ExternalLink className="w-2.5 h-2.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </span>
                <span className="text-slate-500">
                  <span className="font-semibold text-slate-700">{ta.count}</span> req{ta.count === 1 ? '' : 's'}
                  {ta.scored > 0 && (
                    <> · <span className="text-emerald-600 font-medium">{ta.strong}</span> strong</>
                  )}
                </span>
              </div>
              <StackedBar
                distribution={ta.distribution}
                total={ta.count}
                dataTestIdPrefix={`rollup-${ta.name.replace(/\s+/g, '-')}`}
              />
              {/* Sub-areas — only shown for named task areas with named sub-areas */}
              {!ta.is_unassigned && ta.sub_areas && ta.sub_areas.filter(sa => !sa.is_unassigned).length > 0 && (
                <div className="mt-1.5 ml-3 space-y-1 border-l border-slate-200 pl-2">
                  {ta.sub_areas.filter(sa => !sa.is_unassigned).map(sa => (
                    <div key={sa.name} className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-slate-500 truncate" title={sa.name}>
                        ↳ {sa.name}
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0">
                        {sa.scored}/{sa.count} · {sa.strong_fit_pct}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Wrapper>
          );
        })}
        {data.unassigned_count > 0 && taskAreas.every(ta => !ta.is_unassigned) && (
          <p className="text-[10px] text-slate-400 italic pt-1 px-2">
            {data.unassigned_count} requirement{data.unassigned_count === 1 ? '' : 's'} without a Task Area
          </p>
        )}
      </CardContent>
      {/* v3.18.4 — Bulk-bucket Unassigned dialog */}
      <BulkBucketDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        pursuitId={pursuitId}
        onApplied={triggerRefresh}
      />
    </Card>
  );
}
